import React from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";

import { cn } from "@/lib/utils";

interface ToggleAnswersButtonProps {
  /** Whether every answer is currently expanded. */
  isOpen: boolean;
  onToggle: () => void;
  className?: string;
}

/**
 * Shared "show / hide all answers" toggle used on the package phase listings
 * (questions, themes, quizzes) and the author question / theme listings:
 * neutral when collapsed, blue-tinted when every answer is open.
 *
 * The icon + label live in an inner flex span: Reboot.css forces
 * `-webkit-appearance: button`, which stops WebKit from laying out the
 * button's own children as a flex row.
 */
const ToggleAnswersButton: React.FC<ToggleAnswersButtonProps> = ({
  isOpen,
  onToggle,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={isOpen}
      className={cn(
        "group appearance-none inline-block self-start sm:self-auto rounded-xl border px-3.5 py-2 text-left",
        "text-sm font-semibold transition-all duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
        "dark:focus-visible:ring-offset-gray-950",
        isOpen
          ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300 dark:hover:bg-blue-900"
          : "border-gray-200 bg-white text-gray-600 shadow-sm hover:-translate-y-0.5 hover:border-blue-200 hover:text-blue-600 hover:shadow-md dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-blue-900 dark:hover:text-blue-400",
        className
      )}
    >
      <span className="flex items-center gap-2 whitespace-nowrap leading-none">
        {isOpen ? (
          <EyeOff className="h-4 w-4 flex-shrink-0" />
        ) : (
          <Eye className="h-4 w-4 flex-shrink-0 transition-transform group-hover:scale-110" />
        )}
        {isOpen ? t("gameRounds.hideAllAnswers") : t("gameRounds.showAllAnswers")}
      </span>
    </button>
  );
};

export default ToggleAnswersButton;
